import React from 'react';

// Destiny Digits — refined emblem logo
// A gold circular ring with the nine numerology digits orbiting,
// an 8-point compass star at the center and elegant wordmark.
const Logo = ({ variant = 'dark', size = 'md', showText = true }) => {
  const isLight = variant === 'light';
  const sizes = {
    sm: { mono: 40, title: 'text-base', sub: 'text-[8px]' },
    md: { mono: 52, title: 'text-[19px]', sub: 'text-[9px]' },
    lg: { mono: 72, title: 'text-2xl', sub: 'text-[10px]' },
  };
  const s = sizes[size];

  // Place digits 1..9 evenly around the ring
  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const radius = 40;
  const cx = 50;
  const cy = 50;

  return (
    <div className="flex items-center gap-3 select-none">
      <svg width={s.mono} height={s.mono} viewBox="0 0 100 100" fill="none">
        <defs>
          <linearGradient id="dd-gold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f1deac" />
            <stop offset="45%" stopColor="#d4af37" />
            <stop offset="100%" stopColor="#9c7a26" />
          </linearGradient>
          <radialGradient id="dd-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Soft inner glow */}
        <circle cx={cx} cy={cy} r="44" fill="url(#dd-glow)" />

        {/* Outer hairline ring */}
        <circle cx={cx} cy={cy} r="47" stroke="url(#dd-gold)" strokeWidth="0.6" />
        {/* Main ring */}
        <circle cx={cx} cy={cy} r="42" stroke="url(#dd-gold)" strokeWidth="1" />
        {/* Inner ring */}
        <circle cx={cx} cy={cy} r="28" stroke="url(#dd-gold)" strokeWidth="0.6" />

        {/* Numerology digits 1-9 orbit (skipping where compass north sits) */}
        {digits.map((d, i) => {
          const angle = (i / digits.length) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          return (
            <text
              key={d}
              x={x}
              y={y + 2.2}
              textAnchor="middle"
              fontFamily="Playfair Display, serif"
              fontSize="6.2"
              fontWeight="500"
              fill="url(#dd-gold)"
              opacity="0.85"
            >
              {d}
            </text>
          );
        })}

        {/* Compass / 8-point star */}
        <g stroke="url(#dd-gold)" strokeWidth="0.8" fill="none">
          <polygon points="50,22 53,47 78,50 53,53 50,78 47,53 22,50 47,47" fill="url(#dd-gold)" opacity="0.18" />
          <line x1="50" y1="22" x2="50" y2="78" />
          <line x1="22" y1="50" x2="78" y2="50" />
          <line x1="30" y1="30" x2="70" y2="70" opacity="0.55" />
          <line x1="70" y1="30" x2="30" y2="70" opacity="0.55" />
        </g>

        {/* Center diamond */}
        <rect
          x="45"
          y="45"
          width="10"
          height="10"
          transform="rotate(45 50 50)"
          fill="#1a1714"
          stroke="url(#dd-gold)"
          strokeWidth="0.6"
        />
        <text
          x="50"
          y="52.5"
          textAnchor="middle"
          fontFamily="Playfair Display, serif"
          fontSize="7"
          fontWeight="700"
          fill="url(#dd-gold)"
        >
          DD
        </text>

        {/* Cardinal accent dots */}
        <circle cx="50" cy="3.5" r="1.1" fill="#d4af37" />
        <circle cx="50" cy="96.5" r="1.1" fill="#d4af37" />
        <circle cx="3.5" cy="50" r="1.1" fill="#d4af37" />
        <circle cx="96.5" cy="50" r="1.1" fill="#d4af37" />
      </svg>

      {showText && (
        <div className="leading-tight">
          <div
            className={`font-display font-semibold tracking-[0.04em] ${s.title} ${
              isLight ? 'text-[#faf7f0]' : 'text-[#1a1714]'
            }`}
          >
            Destiny <span className="gold-text italic font-cormorant">Digits</span>
          </div>
          <div
            className={`${s.sub} tracking-[0.4em] uppercase font-medium mt-0.5 ${
              isLight ? 'text-[#c9a961]' : 'text-[#a8842c]'
            }`}
          >
            by Pujah Chopraa
          </div>
        </div>
      )}
    </div>
  );
};

export default Logo;
